<template>
    <div
        v-if="creator"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div
            class="relative flex max-h-[560px] w-full max-w-md flex-col items-center rounded-lg bg-white p-6 shadow-md dark:bg-semiblack">
            <button @click="() => emit('cancel')">
                <font-awesome-icon
                    :icon="faClose"
                    class="absolute right-4 top-4 h-6 w-6 cursor-pointer text-gray-600"
                    :disabled="isLoading" />
            </button>
            <img
                v-if="creator.avatar"
                :src="creator.avatar"
                alt="Fan Fiction"
                class="h-40 w-40 rounded-full" />
            <div class="flex h-40 w-40 items-center justify-center rounded-full bg-gray-300" v-else>
                <font-awesome-icon :icon="faUser" class="h-16 w-16 text-gray-500" />
            </div>
            <h3 class="mb-4 text-lg font-semibold dark:text-gray-300">{{ creator?.name }}</h3>
            <scrollable class="flex flex-col items-center" v-if="!showSucessMesage">
                <p class="mb-4 dark:text-gray-300">
                    Want {{ creator?.name }} to read your fantasy?
                </p>
                <div class="mb-4 w-full dark:text-gray-300">
                    <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Send a tip (optional)
                    </label>
                    <input
                        v-model="tipAmount"
                        type="number"
                        step="0.01"
                        min="0"
                        class="w-full rounded-md border border-gray-300 p-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{
                            'border-red-500': errors.tip_amount,
                        }"
                        placeholder="$"
                        @input="clearTipError" />
                    <LaravelErrorMessage :errors="errors.tip_amount" v-if="errors.tip_amount" />
                </div>
                <div class="mb-4 w-full">
                    <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Add a personal note (optional)
                    </label>
                    <textarea
                        v-model="customNote"
                        class="w-full rounded-md border border-gray-300 p-2 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{
                            'border-red-500': errors.custom_note,
                        }"
                        :placeholder="
                            'Tell ' + creator?.name + ' about your fantasy... (max 300 characters)'
                        "
                        rows="3"
                        maxlength="300"
                        @input="clearNoteError"></textarea>
                    <LaravelErrorMessage :errors="errors.custom_note" v-if="errors.custom_note" />
                </div>

                <div
                    v-if="errors.balance"
                    class="mb-4 flex flex-col items-center px-4 text-red-600">
                    <LaravelErrorMessage :errors="errors.balance" />
                </div>
                <LaravelErrorMessage :errors="errors.fantasy_id" v-if="errors.fantasy_id" />
                <LaravelErrorMessage :errors="errors.general" v-if="errors.general" />
                <div class="flex justify-end space-x-2">
                    <button
                        v-if="errors.balance"
                        class="hover:bg-mintgreen-600 mr-auto rounded bg-mintgreen px-2 py-1 text-black"
                        @click.stop="handleBuyCredits">
                        Add Funds
                    </button>

                    <button
                        class="hover:bg-mintgreen-600 rounded bg-mintgreen px-4 py-2 text-black"
                        :class="{ 'cursor-not-allowed opacity-50': isLoading }"
                        @click.stop="sendToModel"
                        :disabled="isLoading">
                        {{
                            creator?.fantasy_is_free
                                ? 'SEND'
                                : 'Send for ' + creator?.fantasy_amount
                        }}
                    </button>
                </div>
            </scrollable>
            <div v-else class="text-center text-green-600">
                <p class="mb-4">Thank you! Your fantasy has been sent to {{ creator?.name }}.</p>
                <p class="text-sm">You will be redirected shortly...</p>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { onBeforeUnmount, onMounted, onUnmounted, ref } from 'vue'
    import { faClose, faUser } from '@fortawesome/free-solid-svg-icons'
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
    import LaravelErrorMessage from '@/Components/LaravelErrorMessage.vue'
    import Scrollable from '@/Components//ui-components/Scrollable.vue'

    const props = defineProps({
        creator: {
            type: Object,
            required: true,
        },
        fantasyId: {
            type: Number,
            required: true,
        },
    })

    const handleBuyCredits = () => {
        // Navigate to the add balance page and return here.
        localStorage.setItem('openSendToModel', props.fantasyId)
        window.location.href =
            '/profile/wallet?return_url=' +
            encodeURIComponent('/fantasy-characters-redirect-send-to-model/' + props.fantasyId)
    }

    const emit = defineEmits(['cancel', 'success'])

    const showSucessMesage = ref(false)
    const errors = ref({})
    const isLoading = ref(false)
    const customNote = ref('')
    const tipAmount = ref('')

    const clearNoteError = () => {
        if (errors.value.custom_note) {
            delete errors.value.custom_note
        }
    }
    const clearTipError = () => {
        if (errors.value.tip_amount) {
            delete errors.value.tip_amount
        }
    }

    onMounted(() => {
        localStorage.removeItem('openSendToModel')
    })

    onUnmounted(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    })

    const sendToModel = () => {
        isLoading.value = true

        if (!props.fantasyId) {
            console.error('Fantasy ID is required to send to model')
            isLoading.value = false
            return
        }

        axios
            .post('/fantasy-characters-send-to-model', {
                fantasy_id: props.fantasyId,
                custom_note: customNote.value || null,
                tip_amount: tipAmount.value || null,
            })
            .then(response => {
                showSucessMesage.value = true
                emit('success')
                // Keep the redirect as fallback if parent doesn't handle success
                setTimeout(() => {
                    localStorage.removeItem('openSendToModel')
                    window.location.href = response.data.redirectUrl
                }, 3000)
            })
            .catch(error => {
                console.error('Error sending to model:', error)
                errors.value = error.response.data.errors || {
                    fantasy_id: ['An error occurred while sending to model.'],
                }
            })
            .finally(() => {
                isLoading.value = false
            })
    }
</script>
